import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, CheckCircle, Shield, FileText, Users, Lock } from "lucide-react";
import { Navbar } from "@/components/layout/Navbar";
import { Footer } from "@/components/layout/Footer";
import { SEOHead } from "@/components/SEOHead";
import { Button } from "@/components/ui/button";

const engagementIncludes = [
  {
    icon: FileText,
    title: "Authority System Design",
    description: "Complete authority architecture tailored to your business model.",
  },
  {
    icon: Users,
    title: "Operator Certification Framework",
    description: "Training and certification program for system operators.",
  },
  {
    icon: Shield,
    title: "Governance & Review Setup",
    description: "Internal review dashboards, approval workflows, and compliance systems.",
  },
  {
    icon: Lock,
    title: "Buyer-Ready Documentation",
    description: "All systems documented to withstand acquisition diligence.",
  },
];

const EngagementPage = () => {
  return (
    <div className="min-h-screen bg-[#080808]" data-testid="engagement-page">
      <SEOHead 
        title="Authority Infrastructure Engagements"
        description="Enterprise-grade authority systems built for founders preparing for scale or exit."
        url="https://onpointauthority.com/engagement"
      />
      <Navbar />

      {/* Hero Section */}
      <section className="pt-32 pb-16 md:pt-40 md:pb-24 px-6 md:px-12" data-testid="engagement-hero">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-4xl"
          >
            <p className="text-xs font-medium tracking-[0.2em] uppercase text-muted-foreground mb-6">
              Offer Structure
            </p>
            <h1 className="font-serif text-5xl md:text-7xl font-light tracking-tight leading-[0.95] text-foreground mb-8">
              Authority<br />
              <span className="text-primary">Engagements</span>
            </h1>
            <p className="text-lg md:text-xl font-light leading-relaxed text-muted-foreground max-w-2xl">
              A full authority infrastructure build, not consulting hours.
            </p>
          </motion.div>
        </div>
      </section>

      {/* What You Engage */}
      <section className="py-16 md:py-24 px-6 md:px-12 border-t border-white/5" data-testid="what-you-engage">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="mb-16"
          >
            <p className="text-xs font-medium tracking-[0.2em] uppercase text-muted-foreground mb-4">
              What You Engage
            </p>
            <h2 className="font-serif text-4xl md:text-5xl font-normal tracking-tight text-foreground">
              Full Infrastructure<br />
              <span className="text-primary">Build</span>
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {engagementIncludes.map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="bg-card/5 border border-white/5 p-8"
                data-testid={`engagement-item-${index}`}
              >
                <item.icon className="h-8 w-8 text-primary mb-4" strokeWidth={1.5} />
                <h3 className="font-serif text-xl text-foreground mb-3">{item.title}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">{item.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Positioning */}
      <section className="py-24 md:py-32 px-6 md:px-12 bg-[#050505]" data-testid="positioning-section">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-center"
          >
            <p className="text-xs font-medium tracking-[0.2em] uppercase text-muted-foreground mb-4">
              Positioning
            </p>
            <h2 className="font-serif text-4xl md:text-5xl font-normal tracking-tight text-foreground mb-8">
              Limited<br />
              <span className="text-primary">Engagements</span>
            </h2>
            
            <div className="space-y-6 text-lg font-light text-muted-foreground leading-relaxed mb-10">
              <p>
                Engagements are limited. This is infrastructure work, not implementation support.
              </p>
              <p className="text-foreground">
                We build systems that persist. We do not offer ongoing consulting.
              </p>
            </div>

            <div className="border border-white/10 bg-card/5 p-8 mb-10">
              <p className="text-sm text-muted-foreground mb-4">What This Is NOT:</p>
              <div className="flex flex-wrap justify-center gap-4">
                {["Coaching", "Consulting hours", "Retainer work", "Implementation support"].map((item, index) => (
                  <span key={index} className="text-xs px-3 py-1 border border-white/10 text-muted-foreground">
                    {item}
                  </span>
                ))}
              </div>
            </div>

            <Link to="/authority-review">
              <Button
                className="bg-primary text-primary-foreground hover:bg-primary/90 uppercase tracking-[0.15em] text-xs font-bold px-10 py-6 rounded-none gold-glow"
                data-testid="engagement-cta"
              >
                Request Authority Review
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default EngagementPage;
